<template>
  <ModalComponent :show="show" @close="$emit('close')" class="import-markings-dialog__modal">
    <CardV2 variant="prompt" :title="$t('importMarkingFiles')">
      <template #default>
        <div class="import-markings-dialog">
          <p v-html="$t('flatfinder:importMarkings:intro')" />
          <ul>
            <li v-html="$t('flatfinder:importMarkings:marking')" />
            <li v-html="$t('flatfinder:importMarkings:naming')" />
          </ul>
          <table class="table table--default table--padding">
            <tbody>
              <tr>
                <td>A-101.png</td>
                <td>← {{ $t('flatfinder:import:markingForUnit', { unit: 'A-101' }) }}</td>
              </tr>
              <tr>
                <td>A-102.png</td>
                <td>← {{ $t('flatfinder:import:markingForUnit', { unit: 'A-102' }) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="result" class="import-markings-dialog__result" :class="resultClass">
            <span>{{ resultMessage }}</span>
            <ul v-if="result.unmatched.length">
              <li v-for="name in result.unmatched" :key="name">{{ name }}</li>
            </ul>
          </div>
        </div>
      </template>
      <template #actions>
        <ButtonComponent
          :label="imported ? $t('close') : $t('cancel')"
          :icon="['fal', 'times']"
          @click="$emit('close')"
        />
        <ButtonComponent
          theme="primary"
          :label="$tc('selectFile', 2)"
          :icon="['fal', 'arrow-down-to-dotted-line']"
          :promise="importing"
          :class="{ 'elder-button--disabled': !currentFrameImage }"
          @click="run"
        />
      </template>
    </CardV2>
  </ModalComponent>
</template>

<script>
import { ButtonComponent } from 'vue-elder-button'
import { ModalComponent } from 'vue-elder-modal'
import CardV2 from '@/components/Card-v2'
import { getItemsFromMasks } from '@/components/Flatfinder/Importer/utils'

export default {
  props: {
    show: Boolean,
    project: String,
    frames: {
      type: Array,
      default: () => [],
    },
    current: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      importing: null,
      result: null,
      imported: false,
    }
  },
  watch: {
    // The dialog stays mounted between openings — start each one clean
    show(value) {
      if (!value) return
      this.result = null
      this.imported = false
    },
  },
  computed: {
    currentFrameImage() {
      return this.$path('image.url', this.frames[this.current])
    },
    resultClass() {
      return `import-markings-dialog__result--${this.result.matched ? 'partial' : 'error'}`
    },
    resultMessage() {
      if (!this.result.matched) return this.$t('flatfinder:importMarkings:noMatch')
      return this.$t('flatfinder:importMarkings:result', this.result)
    },
  },
  methods: {
    run() {
      const frame = this.frames[this.current]
      if (!frame || !frame.image) return

      this.result = null

      return (this.importing = getItemsFromMasks({
        project: this.project,
        dimensions: frame.image.dimensions,
      }).then(picked => {
        // Picker cancelled — keep the dialog open
        if (!picked) return

        const { items, unmatched } = picked
        this.result = { matched: items.length, total: items.length + unmatched.length, unmatched }

        // Nothing resolved — stay open so the naming rules are still on screen
        if (!items.length) return

        this.$set(frame, 'items', [...(frame.items || []), ...items])
        this.imported = true

        // Some files missed — stay open and name them, so they can be renamed and retried
        if (unmatched.length) return

        this.$emit('close')
      }))
    },
  },
  components: {
    ButtonComponent,
    ModalComponent,
    CardV2,
  },
}
</script>

<style lang="scss">
.import-markings-dialog {
  table {
    margin-top: 1rem;
  }

  &__result {
    margin-top: 1rem;
    padding: 0.85rem 1.1rem;
    border-radius: var(--border-radius-sm);
    font-size: 0.9em;

    ul {
      margin: 0.5rem 0 0;
      padding-left: 1.1rem;
    }

    &--error {
      background-color: css-alpha('error', 0.08);
    }

    &--partial {
      background-color: css-alpha('warning', 0.12);
    }
  }

  &__modal {
    .card-v2 {
      width: 620px;
      max-width: 96vw;
    }

    .card-v2__actions {
      justify-content: flex-start;

      > :last-child {
        margin-left: auto;
      }
    }
  }
}
</style>
